<template>
  <div v-if="isLoading" class="signWrap">
    <div class="backBg"></div>
    <div class="container flex col f-center gap-32">
      <div class="signTop">
        <NuxtLink :to="{name: 'index'}" class="logo">
          <img src="~/assets/images/logo_blue.svg" alt="codepresso logo" />
        </NuxtLink>
      </div>
      <LazyRegisterForm
        v-if="registerType === RegisterType.ACCOUNT"
        :all-country-list="countryList"
        :nick-name="nickName"
        @continue="onClickContinue"
      />
      <LazyRegisterOrgForm
        v-else-if="registerType === RegisterType.ORG"
        :nick-name="nickName"
        @continue="onClickOrgContinue"
      />
      <LazySignCommonFooter />
    </div>
  </div>
</template>

<script setup lang="ts">
import {onBeforeMount, ref, useRouter} from '#imports';
import {useUserService} from '~/composables/service/useUserService';
import {useUserStore} from '~/store/user/userStore';
import {CountryModel} from '~~/package/mapper/user/model/CountryModel';
import {useProfileService} from '~/composables/service/useProfileService';
import {ProfileUserInformationModel} from '~~/package/mapper/profile/model/ProfileUserInformationModel';
import {RegisterType} from '~/components/Register/enum/RegisterType';
import {CookieKeyType} from '~~/package/mapper/common/model/CookieKeyType';
import {getCookie, removeCookie} from '~~/package/utils/CookieUtil';
import {useOrganizationService} from '~/composables/service/useOrganizationService';
import {useToastStore} from '~/store/toast/toastStore';
import {ToastType} from '~/components/Common/Toast/model/ToastType';
import {useI18n} from 'vue-i18n';
import {useUtilsStore} from '~/store/utils/utilsStore';

const { t } = useI18n();
const router = useRouter();

const userStore = useUserStore();
const utilsStore = useUtilsStore();
const toastStore = useToastStore();
const userService = useUserService();
const profileService = useProfileService();
const organizationService = useOrganizationService();

const isLoading = ref(false);
const nickName = ref("");

const countryList = ref<CountryModel[]>([]);
const registerType = ref<RegisterType>(RegisterType.ACCOUNT);

onBeforeMount(() => {
  if (!userStore.isLogin()) goToHomePage();
  nickName.value = userStore.userV2.nickName as string;
  initData();
});

const goToHomePage = () => {
  router.replace({ path: "/" });
};

const initData = async () => {
  const hasAuthOrgContext = !!getCookie(CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT);

  if (userStore.userV2.location) {
    if (hasAuthOrgContext) {
      removeCookie(CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT);
      moveToRedirectUrlOrDashboard();
      return;
    }

    // isBlockedEmailDomain() is synchronous; load the list first so a blocked
    // personal-email user isn't misrouted into the org-registration form when
    // the prefetch hasn't resolved yet (e.g. immediately after an SPA login).
    await utilsStore.fetchBlockedEmailDomains();
    if (!userStore.userV2.orgInfo && !utilsStore.isBlockedEmailDomain(userStore.userV2.email)) {
      registerType.value = RegisterType.ORG;
      isLoading.value = true;
    } else {
      moveToRedirectUrlOrDashboard();
    }
  } else {
    userService.getCountryList().then(countries => {
      countryList.value = countries;
      isLoading.value = true;
    });
  }
};


const onClickContinue = ({ firstName, lastName, country, location, allowNotifications }: {
  firstName: string;
  lastName: string;
  country: CountryModel;
  location: string;
  allowNotifications: boolean;
}) => {
  profileService
    .modifyUserProfile({ firstName, lastName } as ProfileUserInformationModel)
    .then((profile) => {
      if (!profile) return;

      Promise.all([
        userService.setUserCountryData(userStore.userV2.id.toString(), country.id.toString()),
        userService.setMarketingTerms(allowNotifications)
      ]).then(async (isSuccessList: [boolean, boolean]) => {
        if (isSuccessList.some((isSuccess) => isSuccess)) {
          userStore.userV2.location = location;
          userStore.userV2.isMarketingConsent = allowNotifications;

          if (getCookie(CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT)) {
            removeCookie(CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT);
            moveToRedirectUrlOrDashboard();
            return;
          }

          // Load the blocklist before the synchronous isBlockedEmailDomain()
          // read so a blocked-email user isn't shown the org-registration form.
          await utilsStore.fetchBlockedEmailDomains();
          if (!userStore.userV2.orgInfo && !utilsStore.isBlockedEmailDomain(userStore.userV2.email)) {
            registerType.value = RegisterType.ORG;
          } else {
            moveToRedirectUrlOrDashboard();
          }
        }
      }).catch(() => {
        toastStore.openToast({
          toastType: ToastType.FAILED,
          contents: t("error-sign-wrong-approach")
        });
      });
    })
    .catch(() => {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t("error-sign-wrong-approach")
      });
    });
};

const onClickOrgContinue = ({ organizationName, range, option }: {
  organizationName: string;
  range: string;
  option: 'create' | 'none';
}) => {
  if (option === 'none') {
    moveToRedirectUrlOrDashboard();
    return;
  }

  organizationService
    .createOrganization(organizationName, range)
    .then((isSuccess: boolean) => {
      if (isSuccess) {
        moveToRedirectUrlOrDashboard();
      } else {
        toastStore.openToast({
          toastType: ToastType.FAILED,
          contents: t('error-sign-wrong-approach'),
        });
      }
    })
    .catch(() => {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t('error-sign-wrong-approach'),
      });
    });
};

const moveToRedirectUrlOrDashboard = () => {
  if (getCookie(CookieKeyType.REDIRECT_URL)) {
    userStore.moveToRedirectUrl();
    removeCookie(CookieKeyType.REDIRECT_URL);
  } else {
    router.replace({ path: "/dashboard" });
  }
};
</script>

<style>
@import "~/assets/scss/form_style.scss";
</style>